/* eslint-disable max-lines */
import {createSelector} from 'reselect';
import {getActiveUsage, UsageItemType} from 'features/wizardMs/reducers/usages/usages';
import {isEmpty} from 'ramda';
import {
    getPackages,
    isEyeglasses,
    validatePackages,
    WizardOptionType,
    WizardValueType,
} from '@optimaxdev/wizard-ms';
import {FILL_IT} from 'constants/wizard/prescriptionType';
import {
    DEFAULT_EYEGLASSES_PACKAGE_INDEX,
    DEFAULT_SUNGLASSES_PACKAGE_INDEX,
} from 'features/wizardMs/constants/packages';
import {getCustomerConfiguration} from 'features/wizardMs/reducers/customerConfiguration/customerConfiguration';
import {NOPRESCRIPTION, PROGRESSIVE_USAGES, PROGRESSIVES} from 'features/wizardMs/constants/usages';
import {getPrescriptionValues} from 'features/wizardMs/selectors/prescription/prescription';
import {checkProgrExpEnabled} from 'features/experiments/progressiveLense/utils';
import {getUsagesItems} from '../usages/usages';
import {getOptionTemplate} from 'selectors/product/product';

/**
 * Returns package node
 * uses for getting title for step 3
 */
export const getPackageNode = createSelector(getActiveUsage, (selectedUsage: UsageItemType | {}):
    | WizardOptionType
    | {} => {
    if (!(selectedUsage as UsageItemType).items) return {};
    const packageNode: WizardOptionType | undefined = ((selectedUsage as UsageItemType)
        .items as WizardOptionType[]).find(
        item =>
            item.alias === (isEyeglasses(selectedUsage as UsageItemType) ? 'package' : 'thickness'),
    );
    return packageNode || {};
});

/**
 * Modifies package items and add to them field isDisabled: false,
 * because without it auto select doesn`t work
 */

export const getProcessedPackageItems = createSelector(
    getActiveUsage,
    getOptionTemplate,
    (selectedUsage: UsageItemType | {}, wizardId): WizardValueType[] => {
        if (!(selectedUsage as UsageItemType).items) return [];

        return getPackages(selectedUsage as UsageItemType, String(wizardId)).map(item => ({
            ...item,
            isDisabled: false,
        }));
    },
);

/**
 * Returns package items
 */

export const getPackageItems = createSelector(
    getProcessedPackageItems,
    getPrescriptionValues,
    getActiveUsage,
    (packages, prescription, usageItem): WizardValueType[] => {
        if (
            prescription &&
            prescription.method === FILL_IT &&
            (usageItem as UsageItemType).sku !== NOPRESCRIPTION
        )
            return validatePackages(prescription, packages);

        return packages;
    },
);

export const getActivePackage = createSelector(
    getPackageItems,
    getCustomerConfiguration,
    (packageItems, configuration): (WizardValueType & {parentNodeId: number}) | {} => {
        return (
            packageItems.find(
                packageItem =>
                    packageItem.node === configuration.options[packageItem.parentNodeId as number],
            ) || {}
        );
    },
);

/**
 * Return boolean value isActiveUsage progressive or not
 */
export const checkProgressiveUsageActive = createSelector(
    getActiveUsage,
    (activeUsage): boolean => 'sku' in activeUsage && PROGRESSIVE_USAGES.includes(activeUsage.sku),
);

/**
 * Update default price for packageItem in experiment.
 * We need to show changing price: 0 +$20 +$40.
 */
export const getPackageItemsForProgrExp = createSelector(
    checkProgressiveUsageActive,
    getPackageItems,
    (isProgrUsageActive, packageItems): WizardValueType[] => {
        if (checkProgrExpEnabled() && isProgrUsageActive) {
            const initialPrice = packageItems[0].price;
            return packageItems.map(item => ({
                ...item,
                price: item.price === initialPrice ? 0 : item.price - initialPrice,
                finalPrice: item.price === initialPrice ? 0 : item.price - initialPrice,
            }));
        }

        return packageItems;
    },
);

// TODO @skokalin This is WRONG way to define autoselected package, because we have 'default: true' field in package
// will be better to leave that function to backend side, but currently it's work not as we expected:
// for eyeglasses: according to backend response we have default selected package is "Value" package instead "Standard" package
// for sunglasses: this is ok, default package should be first from package group

/**
 * Provides the default index to provide autoselect package
 */
export const getDefaultPackageIndex = createSelector(getPackageNode, (packageNode): number =>
    packageNode && (packageNode as WizardOptionType).alias === 'package'
        ? DEFAULT_EYEGLASSES_PACKAGE_INDEX
        : DEFAULT_SUNGLASSES_PACKAGE_INDEX,
);

/**
 * Provides a VIRTUAL selected package ONLY for price endpoint
 * The price endpoint can't calculate price without selected package, so
 * in this case we should get the first not disabled package and return it
 */
export const getActivePackageForPriceEndPoint = createSelector(
    getPackageItems,
    getActivePackage,
    getDefaultPackageIndex,
    (
        packageItems,
        activePackageSelectedFromUI,
        defaultPackageIndex,
    ): (WizardValueType & {parentNodeId: number}) | {} => {
        if (!packageItems.length) return {};
        if (!isEmpty(activePackageSelectedFromUI)) return activePackageSelectedFromUI;

        const foundPackage = packageItems.find((pack, index) => {
            if (index >= defaultPackageIndex) {
                return !('isDisabled' in packageItems[index]) || !packageItems[index].isDisabled;
            }
            return false;
        });
        return foundPackage || {};
    },
);

/**
 * A layer to provide a fake selected package sku if the package still not selected yet
 */
export const getVirtualSelectedPackage = createSelector(
    getPackageItems,
    getActivePackage,
    (packages, activePackage) => {
        if (!isEmpty(activePackage) || packages.length === 0) return {package: null, title: ''};

        return {
            package: {[Number(packages[0].parentNodeId)]: packages[0].node},
            title: packages[0].title,
        };
    },
);

/**
 * Update default price for packageItem.
 * We need to show changing price: 0 +$20 +$40.
 */
export const getPackageItemsWithDefaultPrice = createSelector(
    checkProgressiveUsageActive,
    getPackageItems,
    (isProgressive, packageItems): WizardValueType[] => {
        if (isProgressive) {
            const initialPrice = packageItems[0].price;
            const initialFinalPrice = packageItems[0].finalPrice;
            return packageItems.map(item => {
                return {
                    ...item,
                    price: item.price === initialPrice ? 0 : item.price - initialPrice,
                    finalPrice:
                        item.price === initialFinalPrice ? 0 : item.finalPrice - initialFinalPrice,
                };
            });
        }

        return packageItems;
    },
);

export const getProgressiveValuePackage = createSelector(
    getUsagesItems,
    getOptionTemplate,
    (items, wizardId) => {
        const progressiveUsage = items.find(item => item.sku === PROGRESSIVES);

        if (!progressiveUsage) return '';

        const packages = getPackages(progressiveUsage, String(wizardId));

        return packages[0]?.sku || '';
    },
);
